import type { VirtualComponentConfig, VirtualComponentTypes } from '../../../../types/VirtualComponents';

export type FlowVirtualComponentType = Extract<
	VirtualComponentTypes,
	'button' | 'number' | 'boolean' | 'enum' | 'text'
>;

export interface VirtualComponentDescriptor {
	ref: string;
	parentDeviceId: string;
	parentDeviceName: string;
	componentKey: string;
	type: FlowVirtualComponentType;
	index: number;
	name: string;
	unit?: string;
	min?: number;
	max?: number;
	step?: number;
	maxLength?: number;
	options?: Array<{ value: string; label: string }>;
	defaultValue?: unknown;
	readable: boolean;
	writable: boolean;
}

const SUPPORTED_TYPES = new Set<FlowVirtualComponentType>([
	'button',
	'number',
	'boolean',
	'enum',
	'text',
]);

function values(source: Record<string, any> | any[] | undefined): any[] {
	return Array.isArray(source) ? source : Object.values(source || {});
}

function realDeviceId(id: unknown): string {
	return String(id || '').split('_')[0];
}

function permissions(config: VirtualComponentConfig): { readable: boolean; writable: boolean } {
	const access = String(config?.access || '').trim();
	if (!access || access === '*') return { readable: true, writable: true };
	return {
		readable: access.includes('r') || access.includes('w'),
		writable: access.includes('w'),
	};
}

function componentOptions(
	type: FlowVirtualComponentType,
	config: VirtualComponentConfig,
): Array<{ value: string; label: string }> | undefined {
	const titles = config?.meta?.ui?.titles;
	if (type === 'boolean') {
		return [
			{ value: 'true', label: (Array.isArray(titles) ? titles[1] : undefined) || 'True' },
			{ value: 'false', label: (Array.isArray(titles) ? titles[0] : undefined) || 'False' },
		];
	}
	if (type !== 'enum') return undefined;
	return (config.options || []).map((value) => ({
		value,
		label:
			(titles && !Array.isArray(titles) ? titles[value] : undefined) ||
			(Array.isArray(titles) ? titles[Number(value)] : undefined) ||
			value,
	}));
}

/**
 * Normalizes components from physical parents and extracted virtual devices into one stable list.
 * Runtime settings/status stores are keyed by the real parent id, so duplicate extracted views are
 * deliberately collapsed by `parentDeviceId + componentKey`.
 */
export function resolveVirtualComponentDescriptors(context: {
	devices?: Record<string, any> | any[];
	settings?: Record<string, any>;
	status?: Record<string, any>;
}): VirtualComponentDescriptor[] {
	const devices = values(context.devices);
	const byRealId = new Map<string, any>();
	for (const device of devices) {
		const id = realDeviceId(device?.id);
		if (!id) continue;
		const current = byRealId.get(id);
		if (!current || String(device.id) === id) byRealId.set(id, device);
	}

	const result: VirtualComponentDescriptor[] = [];
	for (const [parentDeviceId, device] of byRealId) {
		const settings = context.settings?.[parentDeviceId] || {};
		const status = context.status?.[parentDeviceId] || {};
		for (const [componentKey, rawConfig] of Object.entries(settings)) {
			const match = componentKey.match(/^(button|number|boolean|enum|text):(\d+)$/);
			if (!match) continue;
			const type = match[1] as FlowVirtualComponentType;
			if (!SUPPORTED_TYPES.has(type)) continue;
			const index = Number(match[2]);
			const config = (rawConfig || {}) as VirtualComponentConfig;
			const access = permissions(config);
			const name = String(config.name || `${type} ${index}`);
			result.push({
				ref: `${parentDeviceId}|${componentKey}`,
				parentDeviceId,
				parentDeviceName: String(device?.name || parentDeviceId),
				componentKey,
				type,
				index,
				name,
				unit: config.meta?.ui?.unit || config.unit,
				min: config.min,
				max: config.max,
				step: config.meta?.ui?.step || config.step,
				maxLength: config.max_len,
				options: componentOptions(type, config),
				defaultValue: config.default_value ?? status?.[componentKey]?.value,
				...access,
			});
		}
	}

	return result.sort((a, b) =>
		`${a.parentDeviceName}\0${a.name}`.localeCompare(`${b.parentDeviceName}\0${b.name}`),
	);
}

export function virtualComponentRuntimeProperty(component: { type?: unknown; index?: unknown }): string {
	return `${String(component.type || '')}${Number(component.index) + 1}`;
}
